/**
 * Listing Condition utilities.
 *
 * Display item condition for second-hand/refurbished product listings.
 *
 * Condition stored in listing.publicData.condition:
 * One of: 'new', 'like_new', 'very_good', 'good', 'acceptable', 'for_parts'
 *
 * Optional: listing.publicData.conditionNotes (free text description)
 *
 * Each condition has a color for visual display and a label.
 */

export const CONDITIONS = [
  { id: 'new', label: 'New', color: '#22c55e', score: 100 },
  { id: 'like_new', label: 'Like New', color: '#84cc16', score: 90 },
  { id: 'very_good', label: 'Very Good', color: '#eab308', score: 75 },
  { id: 'good', label: 'Good', color: '#f97316', score: 60 },
  { id: 'acceptable', label: 'Acceptable', color: '#ef4444', score: 40 },
  { id: 'for_parts', label: 'For Parts', color: '#6b7280', score: 10 },
];

export const CONDITION_IDS = CONDITIONS.map(c => c.id);

/**
 * Get condition object by ID.
 * @param {string} conditionId
 * @returns {Object|null}
 */
export const getCondition = (conditionId) =>
  CONDITIONS.find(c => c.id === conditionId) || null;

/**
 * Get condition from listing.
 * @param {Object|null} listing
 * @returns {Object|null}
 */
export const getListingCondition = (listing) => {
  const conditionId = listing?.attributes?.publicData?.condition;
  return conditionId ? getCondition(conditionId) : null;
};

/**
 * Validate condition ID.
 * @param {string} conditionId
 * @returns {string|null}
 */
export const validateCondition = (conditionId) => {
  if (!conditionId) return 'Condition is required';
  if (!CONDITION_IDS.includes(conditionId)) return `Invalid condition. Must be one of: ${CONDITION_IDS.join(', ')}`;
  return null;
};

/**
 * Filter listings by minimum condition score.
 * @param {Object[]} listings
 * @param {number} minScore
 * @returns {Object[]}
 */
export const filterByCondition = (listings, minScore) => {
  if (!Array.isArray(listings)) return [];
  return listings.filter(l => {
    const cond = getCondition(l.attributes?.publicData?.condition);
    return cond ? cond.score >= minScore : false;
  });
};
